import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import SettingsForm from './SettingsForm';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Кастомная панель раздела «Карты».
 *
 * Состоит из двух частей:
 *   1) Стандартная форма настроек (через переиспользование SettingsForm) —
 *      ключ Яндекса, интервал воркера и т.п.
 *   2) Блок «Не удалось геокодировать» — список контактов с
 *      geocode_error=1, у каждого можно либо вручную задать координаты,
 *      либо «попробовать ещё раз» (на следующем тике воркер заново сходит
 *      в Яндекс).
 *
 * Право на чтение/запись — `settings.maps`. Эндпойнты:
 *   GET    /api/maps/failed-geocodes
 *   PUT    /api/maps/contacts/{id}/coordinates  { coordinates: "lat,lon" }
 *   DELETE /api/maps/contacts/{id}/coordinates
 */
export default function MapsSettingsPanel() {
  const can = useCan();
  const canRead  = can('settings.maps', 'read');
  const canWrite = can('settings.maps', 'write');

  if (!canRead) {
    return (
      <div className="settings-card">
        <h2>Нет доступа</h2>
        <p>У вас нет права на чтение раздела «Карты».</p>
      </div>
    );
  }

  return (
    <>
      <SettingsForm section="maps" />
      <FailedGeocodesPanel canWrite={canWrite} />
    </>
  );
}

function FailedGeocodesPanel({ canWrite }) {
  const [rows, setRows]       = useState(null);
  const [error, setError]     = useState(null);
  const [busyId, setBusyId]   = useState(null);
  // По текущему id'у — то что в инпуте «Координаты вручную».
  const [drafts, setDrafts]   = useState({});

  const reload = () => {
    setError(null);
    api.get('/api/maps/failed-geocodes')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'));
  };

  useEffect(reload, []);

  const setDraft = (id, val) => setDrafts((d) => ({ ...d, [id]: val }));

  const saveManual = async (id) => {
    const v = (drafts[id] || '').trim();
    if (!v) { setError('Введите координаты в формате «широта,долгота»'); return; }
    setBusyId(id); setError(null);
    try {
      await api.put(`/api/maps/contacts/${id}/coordinates`, { coordinates: v });
      // После сохранения убираем строку из списка — она больше не «failed».
      setRows((arr) => (arr || []).filter((r) => r.id !== id));
      setDrafts((d) => { const c = { ...d }; delete c[id]; return c; });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить координаты');
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (id) => {
    setBusyId(id); setError(null);
    try {
      await api.delete(`/api/maps/contacts/${id}/coordinates`);
      // coordinates=NULL, geocode_error=NULL — на следующем тике воркер заново попробует.
      setRows((arr) => (arr || []).filter((r) => r.id !== id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сбросить');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="settings-card" style={{ marginTop: 16 }}>
      <div className="settings-card__head">
        <h2 className="settings-card__title">
          Не удалось геокодировать
          {rows !== null && (
            <span className="muted" style={{ marginLeft: 8, fontWeight: 400 }}>
              · {rows.length}
            </span>
          )}
        </h2>
        <p className="settings-card__sub">
          Воркер сходил в Яндекс, но адрес найти не смог (либо была сетевая ошибка).
          Чтобы повторно не дёргать Яндекс по тому же адресу, такие контакты помечены
          и пропускаются. Здесь можно ввести координаты вручную (скопируйте из
          Яндекс.Карт — «Что здесь?» → координаты) или отправить на повторное
          геокодирование, если ошибка была временной.
        </p>
      </div>

      {error && (
        <div style={{ color: 'var(--red-fg)', fontSize: 12.5, padding: '0 0 8px' }}>{error}</div>
      )}

      {rows === null ? (
        <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Загрузка…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
          Список пуст — всё геокодировано (или ещё не пробовали).
        </div>
      ) : (
        <table className="table" style={{ fontSize: 12.5 }}>
          <thead>
            <tr>
              <th>Ученик</th>
              <th>Контакт</th>
              <th>Адрес</th>
              <th style={{ width: 260 }}>Координаты вручную</th>
              <th style={{ width: 200, textAlign: 'right' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const addr = (r.addressFact || r.addressReg || '').trim();
              const altAddr = (r.addressFact && r.addressReg && r.addressFact !== r.addressReg)
                ? (r.addressFact === addr ? r.addressReg : r.addressFact)
                : null;
              const yaUrl = 'https://yandex.ru/maps/?text=' + encodeURIComponent(addr);
              return (
                <tr key={r.id}>
                  <td>
                    <Link
                      to={`/students?student=${r.student.id}`}
                      style={{
                        color: r.student.status === 'archive' ? 'var(--fg-muted)' : 'var(--link, var(--fg))',
                        textDecoration: 'none',
                      }}
                    >
                      {r.student.name}
                    </Link>
                  </td>
                  <td>{r.fullName || <span className="muted">—</span>}</td>
                  <td>
                    <div>{addr || <span className="muted">—</span>}</div>
                    {altAddr && (
                      <div style={{ color: 'var(--fg-muted)', fontSize: 11 }}>
                        также: {altAddr}
                      </div>
                    )}
                    {addr && (
                      <div style={{ marginTop: 2 }}>
                        <a href={yaUrl} target="_blank" rel="noreferrer"
                           style={{ fontSize: 11, color: 'var(--link, var(--fg))' }}
                           title="Открыть в Яндекс.Картах — там можно подобрать правильную точку и скопировать координаты">
                          открыть в Я.Картах <Icon name="external" size={10} />
                        </a>
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      className="settings-field__input"
                      placeholder="55.7558, 37.6173"
                      value={drafts[r.id] ?? ''}
                      onChange={(e) => setDraft(r.id, e.target.value)}
                      disabled={!canWrite || busyId === r.id}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); saveManual(r.id); }
                      }}
                      style={{ height: 28, fontSize: 12, fontFamily: 'var(--font-mono)' }}
                    />
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => retry(r.id)}
                      disabled={!canWrite || busyId === r.id}
                      title="Сбросить флаг ошибки. На следующем тике воркер попробует заново."
                      style={{ marginRight: 4 }}
                    >
                      Повторить
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => saveManual(r.id)}
                      disabled={!canWrite || busyId === r.id || !(drafts[r.id] || '').trim()}
                      title="Сохранить введённые координаты"
                    >
                      Сохранить
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="settings-card__foot">
        <div className="settings-card__spacer" />
        <button type="button" className="btn btn--sm" onClick={reload}>
          Обновить
        </button>
      </div>
    </div>
  );
}
